import './polyfills'

import React from 'react'
import ReactDOM from 'react-dom'
import * as Sentry from '@sentry/react'
import TagManager from 'react-gtm-module'
import getAuth0ClientConfig from './lib/auth0/get-auth0-client-config'
import Auth0ClientConfigContext from './lib/auth0/auth0-client-config-context'
import AnalyticsContext from './lib/analytics/context'
import LoggerContext from './lib/logger/context'
import consoleAnalytics from './lib/analytics/console'
import gtmAnalytics from './lib/analytics'
import consoleLogger from './lib/logger/console'
import sentryLogger from './lib/logger'
import App from './App'
import Favicons from './components/favicons'
import ErrorPage from './pages/error'
import TaskHeader from './pages/layout/task-header'
import { Cookies, CookieName } from './lib/cookies'

const isSentryEnabled = Boolean(process.env.REACT_APP_SENTRY_DSN)
const logger = isSentryEnabled ? sentryLogger : consoleLogger

if (isSentryEnabled) {
  Sentry.init({
    debug: true,
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENV || 'production',
  })
}

let auth0ClientConfig = null
try {
  auth0ClientConfig = getAuth0ClientConfig(window.auth0Config.clientID)
} catch (error) {
  logger.captureException(error)
}

const isGtmEnabled = Boolean(process.env.REACT_APP_GTM_ID)
const analytics = isGtmEnabled ? gtmAnalytics : consoleAnalytics
const gaConfigString = window.auth0Config.extraParams?.ga_config
const enableManualCrossDomainTracking =
  process.env.REACT_APP_GTM_MANUAL_XDOMAIN_TRACKING_ENABLED == 'true'

if (isGtmEnabled && process.env.REACT_APP_GTM_ID) {
  if (enableManualCrossDomainTracking) {
    // TODO: This can be removed once multi-custom-domain support is implemented in auth0 and utilised within website
    if (auth0ClientConfig) {
      // Setup custom GTM country cookie to circumvent issues when deriving the country from the domain (as NZ also uses
      // .com.au at the time of writing)
      Cookies.set(
        CookieName.AnalyticsCountry,
        auth0ClientConfig.country === 'New Zealand' ? 'nz' : 'au'
      )
    }

    // Initialise custom GTM specific cookies with the source GA client and session IDs to circumvent cross domain issues.
    // This is only required for the initial page load, as the GTM container will then take over and manage the cookies,
    // including their removal once read.
    // TODO: This can be removed once multi-custom-domain support is implemented in auth0 and utilised within website
    if (gaConfigString) {
      try {
        const gaConfig = JSON.parse(atob(gaConfigString))

        // Only set if the config is still valid (i.e. not expired)
        if (gaConfig.expiry > Date.now()) {
          Cookies.set(CookieName.AnalyticsGACID, gaConfig.client_id)
          Cookies.set(CookieName.AnalyticsGASID, gaConfig.session_id)
        }
      } catch (error) {
        // Ignore
        console.error('Unable to parse the provided GA config', error)
      }
    }
  }

  const gtmPreviewArgs =
    process.env.REACT_APP_GTM_PREVIEW_AUTH &&
    process.env.REACT_APP_GTM_PREVIEW_ENV
      ? {
          auth: process.env.REACT_APP_GTM_PREVIEW_AUTH,
          preview: process.env.REACT_APP_GTM_PREVIEW_ENV,
        }
      : {}
  TagManager.initialize({
    gtmId: process.env.REACT_APP_GTM_ID,
    ...gtmPreviewArgs,
  })
}

const { extraParams } = window.auth0Config

const index = auth0ClientConfig ? (
  <>
    <Favicons pathToAssets={process.env.PUBLIC_URL} />
    <AnalyticsContext.Provider value={analytics}>
      <LoggerContext.Provider value={logger}>
        <Auth0ClientConfigContext.Provider value={auth0ClientConfig}>
          <App
            initialScreen={window.auth0Config.extraParams.screen_hint}
            initialNotificationFlags={{
              sessionExpired: extraParams.westfield_session_expired === 'true',
              passwordResetExpired:
                extraParams.westfield_password_reset_expired === 'true',
              passwordResetSuccess:
                extraParams.westfield_password_reset_success === 'true',
              userIsBlocked: extraParams.westfield_user_is_blocked === 'true',
              genericIssue: extraParams.westfield_generic_issue === 'true',
            }}
          />
        </Auth0ClientConfigContext.Provider>
      </LoggerContext.Provider>
    </AnalyticsContext.Provider>
  </>
) : (
  // We must *always* have the auth0 client config, so if in the very unlikely scenario we're unable to retrieve it,
  // something has gone horribly wrong, and in which case, present a very obvious error page
  <>
    <TaskHeader
      back={{ label: 'Back', handleClickBack: () => window.history.back() }}
    />
    <ErrorPage includeLinkToHome={false} />
  </>
)

ReactDOM.render(
  <React.StrictMode>{index}</React.StrictMode>,
  document.getElementById('root')
)
